import htmlToDraft from 'html-to-draftjs';
import { ContentState, EditorState } from 'draft-js';
import _ from 'lodash';
import moment from 'moment';
import { handleDeleteEmbedBug } from '../../../../utils/newBedStatusUtil';
import aesGcmUtil from '../../../../utils/aesGcmUtil';
import {
  buildAEDBedList,
  maskChineseName,
  maskEnglishName,
  normalizeLayoutNumber,
  normalizeNumber,
  parseCubiclePosition,
} from '../../../../utils/admissionUtil';
import { conditional } from '../../../../utils';

const decryptPatientName = async (name) => {
  if (!name) {
    return '';
  }

  try {
    return await aesGcmUtil.decryptMessage(name, 1234567890123456);
  } catch {
    return '';
  }
};

export const revampLocationPatient = async ({ patient, isLogin }) => {
  if (!patient) {
    return patient;
  }

  const [nameZh, nameEn] = await Promise.all([
    decryptPatientName(patient?.chiName),
    decryptPatientName(patient?.engName),
  ]);
  // 未登录时床位和queuebar展示的名字都要加星号。
  const displayNameZh = isLogin ? nameZh : maskChineseName(nameZh);
  const displayNameEn = isLogin ? nameEn : maskEnglishName(nameEn);

  return {
    ...patient,
    age: conditional(patient?.dob, moment().diff(moment(patient?.dob, 'DD-MM-YYYY'), 'years'), ''),
    patientName: {
      nameZh: displayNameZh,
      nameEn: displayNameEn,
    },
    // AED View的病人id是 eaId
    // SOPD View是 transferRequestId
    // 统一用eaId
    eaId: patient?.transferRequestId ?? patient?.eaId,
  };
};

export const revampLocationCubicle = async ({ cubicle, revampPatient }) => {
  const position = parseCubiclePosition(cubicle?.position);
  let remarks = cubicle.remark;
  if (cubicle.cubType === 'Memo') {
    const blocksFromHtml = htmlToDraft(cubicle?.remark ?? '');
    const { contentBlocks, entityMap } = blocksFromHtml;
    const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
    remarks = EditorState.createWithContent(contentState);
  }

  // Memo 和 Nurse Station 依赖空格子撑开显示尺寸。
  const shouldFillEmptyBeds = cubicle?.cubType === 'Memo' || cubicle?.cubType === 'Nurse Station/Misc.';
  const bedList = buildAEDBedList({
    beds: cubicle?.beds,
    cubicleRows: cubicle?.cubRows,
    cubicleCols: cubicle?.cubColumns,
    editMode: false,
    fillEmptyBeds: shouldFillEmptyBeds,
  });
  const newBedList = await Promise.all(
    _.map(bedList, async (bed) => ({
      ...bed,
      patientInfo: await revampPatient(bed?.patientInfo),
    }))
  );

  return {
    width: normalizeLayoutNumber(cubicle?.width),
    height: normalizeLayoutNumber(cubicle?.height),
    x: normalizeLayoutNumber(position?.X ?? position?.x),
    y: normalizeLayoutNumber(position?.Y ?? position?.y),
    id: normalizeNumber(cubicle?.cubicleId),
    data: {
      name: cubicle?.name,
      type: cubicle?.cubType ?? '',
      status: cubicle?.status ?? '',
      gender: cubicle?.gender ?? '',
      color: cubicle?.cubColour,
      remarks,
      remarkColor: cubicle?.rmkColour,
      bedArrangement: { column: cubicle?.cubColumns, row: cubicle?.cubRows },
      borderLine: cubicle?.borderLine,
      header: cubicle?.header,
      beds: newBedList,
      version: cubicle?.version,
    },
  };
};

export const buildLocationCubicleUpdateParams = (cubicleData) => {
  const cubicleInfo = cubicleData?.data;

  return {
    cubType: cubicleInfo?.type,
    borderLine: cubicleInfo?.borderLine,
    cubColour: cubicleInfo?.color,
    cubicleId: cubicleData?.id,
    gender: cubicleInfo?.gender,
    header: cubicleInfo?.header,
    name: cubicleInfo?.name,
    remark: cubicleInfo.type === 'Memo' ? handleDeleteEmbedBug(cubicleInfo.remarks) : cubicleInfo.remarks,
    rmkColour: cubicleInfo?.remarkColor,
    status: cubicleInfo?.status,
    version: cubicleInfo?.version,
  };
};
